/* eslint-disable react/jsx-props-no-spreading */
// We are using a form validating lib that relies on spreading props

import React from 'react';
import { useForm } from 'react-hook-form';
import TagManager from 'react-gtm-module';
import styled from 'styled-components';

import {
	LoginModal as LoginModalCore,
	TextInput,
	TextInputRevealPassword,
	Button,
	ErrorMessage,
	useScreenQuery,
} from 'playup-web-core';

import useOpenModal from '../../../hooks/useOpenModal';
import appConfig from '../../../appConfig';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import { parseApiError } from '../../../utils/parseApiError';
import ResponsibleGambling from '../ResponsibleGambling';

interface FormData {
	email: string;
	password: string;
}

const fantasyEnabled = appConfig.fantasy.enabled;
const isBooki = appConfig.brand.name === 'Booki';

const LoginModal = ({ smallScreen = false, showRG = false, onClose }: Props) => {
	const [sending, setSending] = React.useState<boolean>(false);
	const [errors, setErrors] = React.useState<APIError>();
	const { register, handleSubmit } = useForm<FormData>();
	const { login, user } = React.useContext(PortalSessionContext);
	const { isSmall } = useScreenQuery();

	const handleOpenModal = useOpenModal();

	if (user?.id) {
		return null;
	}

	const onSubmit = (data: FormData) => {
		setSending(true);

		login(data.email, data.password)
			.then((response) => {
				if (appConfig.googleTagManagerID)
					TagManager.dataLayer({
						dataLayer: {
							event: 'login',
						},
					});

				onClose();
			})
			.catch((error) => {
				if (error?.meta?.require_password_reset) {
					handleOpenModal('reset-password', { email: error.meta.email });
				}
				setErrors(error.errors);
			})
			.finally(() => {
				setSending(false);
			});
	};

	return (
		<LoginModalCore
			title={fantasyEnabled ? 'LOG IN TO A SINGLE ACCOUNT TO BET AND PLAY' : 'LOG IN'}
			isMultiBrands={!isBooki && fantasyEnabled}
			onClose={onClose}
		>
			<StyledForm onSubmit={handleSubmit(onSubmit)}>
				<StyledTextInput
					kind="white"
					autoComplete="username"
					type="text"
					label="Username"
					autoCapitalize="off"
					{...register('email', { required: true })}
					placeholder="Username or Email Address"
					error={parseApiError(errors, 'email')}
				/>
				<StyledTextInputRevealPassword
					kind="white"
					autoComplete="current-password"
					type="password"
					label="Password"
					{...register('password', { required: true })}
					placeholder="Your password"
					error={parseApiError(errors, 'password')}
				/>
				{!isBooki && (
					<div className=" _text-sm">
						This site is protected by reCAPTCHA and the Google
						<a
							target="_blank"
							className=" _text-blue-400 _pl-1"
							href="https://policies.google.com/privacy"
							rel="noreferrer"
						>
							Privacy Policy
						</a>{' '}
						and
						<a
							target="_blank"
							className=" _text-blue-400 _pl-1"
							href="https://policies.google.com/terms"
							rel="noreferrer"
						>
							Terms of Service
						</a>{' '}
						apply.
					</div>
				)}

				<LoginButton isSmallScreen={isSmall} kind="white" type="submit" height={50} isLoading={sending}>
					Login
				</LoginButton>
				{errors && errors.error && <ErrorMessage>{parseApiError(errors)}</ErrorMessage>}
				<UserOperationSection>
					<ForgotDetailsButton type="button" onClick={() => handleOpenModal('forgot-password')}>
						<ForgotDetailsButtonText isSmallScreen={isSmall}>Forgot Details</ForgotDetailsButtonText>
					</ForgotDetailsButton>
				</UserOperationSection>
				<UserRegistrationSection isSmallScreen={isSmall}>
					<NormalText isSmallScreen={isSmall}>Don't have an account?</NormalText>
					<JoinNowButton
						isSmallScreen={isSmall}
						kind="hollow-black"
						height={50}
						onClick={() => handleOpenModal('signup')}
					>
						Join Now
					</JoinNowButton>
				</UserRegistrationSection>
				{showRG && <ResponsibleGambling />}
			</StyledForm>
		</LoginModalCore>
	);
};

export default LoginModal;

interface Props {
	onClose: () => void;
	smallScreen?: boolean;
	showRG?: boolean;
}

const StyledForm = styled.form({
	width: '100%',
});

const StyledTextInput = styled(TextInput)({
	marginBottom: '20px',
});

const StyledTextInputRevealPassword = styled(TextInputRevealPassword)({
	marginBottom: '20px',
});

const LoginButton = styled(Button)<{ isSmallScreen: boolean }>(({ isSmallScreen }) => ({
	marginTop: isSmallScreen ? '30px' : '50px',
}));

const UserOperationSection = styled.section({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'flex-end',
	alignItems: 'center',
	margin: '10px 0',
});

const NormalText = styled.p<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	color: theme.colors.darkR,
	fontSize: isSmallScreen ? '14px' : '18px',
	margin: isSmallScreen ? '10px 0' : '5px 0',
	fontWeight: 600,
}));

const ForgotDetailsButton = styled.button`
	&:focus {
		outline: none;
	}
`;

const ForgotDetailsButtonText = styled(NormalText)(({ theme }) => ({
	color: theme.colors.blue,
}));

const UserRegistrationSection = styled.section<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-around',
	alignItems: 'center',
	marginTop: isSmallScreen ? '30px' : '50px',
}));

const JoinNowButton = styled(Button)<{ isSmallScreen: boolean }>(({ isSmallScreen }) => ({
	width: isSmallScreen ? '170px' : '225px',
}));
